import { useEffect, useRef, useState } from 'react';
import { useAuth, api } from 'src/utils';
import PageNotFound from '../404';
import { Box, Card, CardContent, CardHeader, CircularProgress, Grid, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, TablePagination, Typography, Paper } from 'src/components';

export default function AdminPortal() {
    const { user } = useAuth();
    const hasRequestedData = useRef(false);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        if (!user || hasRequestedData.current) return;
        hasRequestedData.current = true;

        const fetchData = async () => {
            try {
                const res = await api.get('/admin');
                setData(res.data);
            } catch (err) {
                console.error("Error fetching data", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [user]);

    if (!user || user.role !== "Admin") {
        return <PageNotFound />;
    }


    if (loading) {
        return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress />
        </Box>
        );
    }

    if (!data) {
        return (
        <Typography align="center" variant="h6" mt={4}>
            No data available.
        </Typography>
        );
    }

    const renderTable = (rows, title) => {
        const handleChangePage = (event, newPage) => setPage(newPage);
        const handleChangeRowsPerPage = (event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
        };

        const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

        return (
            <Card sx={{ boxShadow: 3, borderRadius: 3 }}>
            <CardHeader title={title} />
            <CardContent>
                <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                    <TableRow>
                        <TableCell><strong>User</strong></TableCell>
                        <TableCell><strong>Text</strong></TableCell>
                        <TableCell><strong>Created</strong></TableCell>
                    </TableRow>
                    </TableHead>
                    <TableBody>
                    {paginatedRows.map((row) => (
                        <TableRow 
                        key={row.id} 
                        hover 
                        sx={{ color: row.isDeleted ? 'warning.main' : 'inherit' }}
                        >
                        <TableCell sx={{ color: row.isDeleted ? 'warning.main' : 'inherit' }}>
                            {row.userId}
                        </TableCell>
                        <TableCell sx={{ color: row.isDeleted ? 'warning.main' : 'inherit' }}>
                            {row.text}
                        </TableCell>
                        <TableCell sx={{ color: row.isDeleted ? 'warning.main' : 'inherit' }}>
                            {new Date(row.createdAt).toLocaleString()}
                        </TableCell>
                        </TableRow>
                    ))}
                    {rows.length === 0 && (
                        <TableRow>
                        <TableCell colSpan={3} align="center">
                            No {title.toLowerCase()} available
                        </TableCell>
                        </TableRow>
                    )}
                    </TableBody>
                </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={rows.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
            </CardContent>
            </Card>
        );
    };

    return (
        <Box p={4}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
            Admin Dashboard
        </Typography>

        <Grid container spacing={4}>
            <Grid>
                {renderTable(data.reports, "Reports")}
            </Grid>
            <Grid>
                {renderTable(data.comments, "Comments")}
            </Grid>
        </Grid>
        </Box>
    );
}
